/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect, Fragment } from "react";
import { FiPlay, FiUpload } from "react-icons/fi";
import { useAuth } from "../../auth/AuthProvider";
import { jwtDecode } from "jwt-decode";
import Papa, { ParseResult } from "papaparse";

import { Field } from "../../features/projects/components/Field";
import { ProjectStatusToggle } from "../../features/projects/components/ProjectStatusToggle";
import { GenProfileUpload } from "../../features/projects/components/GenProfileUpload";
import { ENTRY_KEYS } from "../../features/projects/domain/types";
import { validateInputs } from "../../features/projects/domain/validation";
import { buildProjectPayload } from "../../features/projects/domain/payload";
import { createProject, queueRun, getLatestRun } from "../../api/projects";
import { API } from "../../api/api";

/* ——— Types ——— */
export interface ModelInputs {
  /* ── identification ── */
  customer_id: string;
  project_name: string;
  /* ── location ── */
  latitude: number | "";
  longitude: number | "";
  state: string;
  county: string;
  city: string;
  zip_code: string;
  timezone: string;
  /* ── codes ── */
  project_nerc_code: string;
  eia_ba_code: string;
  /* ── dates ── */
  cod_date: string | "";
  decommision_date: string | "";
  /* ── numeric ── */
  nameplate_capacity: number | "";
  poi_limit: number | "";
  project_additional_load: number | "";
  ppa_percentage: number | "";
  ppa_capacity: number | "";
  battery_capacity_mwh: number | "";
  battery_charge_rate_mw: number | "";
  battery_discharge_rate_mw: number | "";
  battery_round_trip_eff: number | "";
  battery_soc_min: number | "";
  battery_chemistry: string;
  battery_calendar_life_years: number | "";
  battery_cycle_life: number | "";
  battery_augmentation_flag: boolean;
  battery_depth_of_discharge: number | "";
  project_lifetime_years: number | "";
  /* ── flags ── */
  existing_project_flag: boolean;
  sam_profile_flag?: boolean; // optional, but included in payload
  ppa_flag: boolean;
  battery_flag: boolean;
  /* ── new required fields ── */
  tech_type_id: string; // <-- added
  gen_hour_profile_mw: { timestamp: string; value: number | null }[]; // <-- added
}


// Add this type for model run results
type JsonMap = Record<string, any>;
interface ModelResults {
  status: string;
  summary_json: {
    Impacts?: {
      Operational_Analysis?: JsonMap;
      Cradle_to_Grave_Analysis?: JsonMap;
    };
    Emissions?: {
      Operational_Analysis?: JsonMap;
    };
    Generation?: JsonMap;
  };
}

/* ——— Quick‑test defaults ——— */
const DEFAULT_INPUTS: ModelInputs = {
  customer_id: process.env.REACT_APP_CUSTOMER_ID || "",
  project_name: "Test_Project_63",
  latitude: 40.1164,
  longitude: -88.2434,
  state: "TX",
  county: "Default",
  city: "Default",
  zip_code: "00000",
  timezone: "America/Chicago",
  project_nerc_code: "US-WECC",
  eia_ba_code: "MISO",
  cod_date: "2024-01-01",
  decommision_date: "",
  nameplate_capacity: 165.0,
  poi_limit: 165.0,
  project_additional_load: 0.0,
  ppa_percentage: 1.0,
  ppa_capacity: 165.0,
  battery_capacity_mwh: 0.0,
  battery_charge_rate_mw: 0.0,
  battery_discharge_rate_mw: 0.0,
  battery_round_trip_eff: 1.0,
  battery_soc_min: 0.0,
  battery_chemistry: "lfp",
  battery_calendar_life_years: 20.0,
  battery_cycle_life: 6000,
  battery_augmentation_flag: false,
  battery_depth_of_discharge: 0.85,
  project_lifetime_years: "",
  existing_project_flag: false,
  sam_profile_flag: true,
  ppa_flag: true,
  battery_flag: false,
  tech_type_id: "solar_ground",
  gen_hour_profile_mw: [],
};

/* ——— Key groups ——— */
const textKeys = [
  "project_name",
  "state",
  "county",
  "city",
  "zip_code",
  "timezone",
  "project_nerc_code",
  "eia_ba_code",
  "tech_type_id",
] as const;

const numberKeys = [
  "latitude",
  "longitude",
  "nameplate_capacity",
  "poi_limit",
  "project_additional_load",
  "ppa_percentage",
  "ppa_capacity",
  "battery_capacity_mwh",
  "battery_charge_rate_mw",
  "battery_discharge_rate_mw",
  "battery_round_trip_eff",
  "battery_soc_min",
] as const;

const dateKeys = ["cod_date", "decommision_date"] as const;

const boolKeys = ["ppa_flag", "battery_flag"] as const;

const GRID = "grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5";

/* ——— Sections for user‑friendly layout ——— */
const SECTIONS: { title: string; keys: readonly (keyof ModelInputs)[] }[] = [
  { title: "Identification", keys: ["project_name"] },
  {
    title: "Location",
    keys: [
      "latitude",
      "longitude",
      "state",
      "county",
      "city",
      "zip_code",
      "timezone",
    ],
  },
  { title: "Codes", keys: ["project_nerc_code", "eia_ba_code", "tech_type_id"] },
  { title: "Dates", keys: ["cod_date", "decommision_date"] },
  {
    title: "Capacity & Limits",
    keys: ["nameplate_capacity", "poi_limit", "project_additional_load"],
  },
  {
    title: "Power Purchase Agreement (PPA)",
    keys: ["ppa_flag", "ppa_percentage", "ppa_capacity"],
  },
  {
    title: "Battery",
    keys: [
      "battery_flag",
      "battery_capacity_mwh",
      "battery_charge_rate_mw",
      "battery_discharge_rate_mw",
      "battery_round_trip_eff",
      "battery_soc_min",
    ],
  },
];

const PROJECT_STATUS_OPTIONS = [
  { label: "Operating", value: "existing" },
  { label: "Prospective", value: "future" },
];

/** Utility to convert a JSON map (possibly object-of-objects or array) into rows */
function flattenForCsv(
  section: string,
  category: string,
  data: JsonMap | JsonMap[]
): Record<string, any>[] {
  if (Array.isArray(data)) {
    return data.map((row) => ({ section, category, ...row }));
  }
  return [{ section, category, ...data }];
}

export default function Model() {
  /* ——— Auth ——— */
  const { token, refreshToken, logout } = useAuth();

  /* ——— State ——— */
  const [inputs, setInputs] = useState<ModelInputs>({ ...DEFAULT_INPUTS, customer_id: "" });
  const [customerId, setCustomerId] = useState<string>("");
  const [isRunning, setIsRunning] = useState(false);
  const [modelResults, setModelResults] = useState<ModelResults | null>(null);
  const [projectStatus, setProjectStatus] = useState<"existing" | "future">("future");
  const [activeTab, setActiveTab] = useState<string>("Impacts: Operational Analysis");

  type RunPhase = 'idle' | 'submitting' | 'running';

  const [runPhase, setRunPhase] = useState<RunPhase>('idle');

  // Add these to your state declarations
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const onField = (name: keyof ModelInputs, value: any) =>
    setInputs(prev => ({ ...prev, [name]: value }));

  /* ——— Effects ——— */
  useEffect(() => {
    if (!token) return;
    try {
      const claims = jwtDecode<{ customer_id: string }>(token);
      // Only set customer ID from JWT if not available in env vars
      if (!process.env.REACT_APP_CUSTOMER_ID) {
        setCustomerId(claims.customer_id);
        setInputs((prev) => ({ ...prev, customer_id: claims.customer_id }));
      } else {
        setCustomerId(process.env.REACT_APP_CUSTOMER_ID);
      }
    } catch (err) {
      console.error("Bad token?", err);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode<{ exp: number }>(token);
      const isExpired = decoded.exp * 1000 <= Date.now();
      if (isExpired) {
        setErrorMessage("Your session has expired. Please log in again.");
        setShowErrorModal(true);
        logout();
      }
    } catch (err) {
      console.error("Invalid token:", err);
    }
  }, []);

  /* ——— Derived completeness ——— */
  const { valid: isComplete } = useMemo(
    () => validateInputs(inputs, { requiredKeys: ENTRY_KEYS, requireGenIfExisting: true }),
    [inputs]
  );

  /* ——— Handlers ——— */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, type, value, checked } = e.target as HTMLInputElement;
    setInputs((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleProjectStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isExisting = e.target.value === "existing";
    setProjectStatus(isExisting ? "existing" : "future");
    setInputs((prev) => ({
      ...prev,
      existing_project_flag: isExisting,
      sam_profile_flag: !isExisting,
    }));
  };

  const handleGenProfileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<any>) => {
        const data = (results.data as any[])
          .map((row) => ({
            timestamp: row.timestamp || row.Timestamp,
            value:
              row.value !== undefined
                ? Number(row.value)
                : row.generation !== undefined
                  ? Number(row.generation)
                  : null,
          }))
          .filter((row) => row.timestamp);
        setInputs((prev) => ({ ...prev, gen_hour_profile_mw: data }));
      },
    });
  };

  const pollForResults = async (projectId: string) => {
    setIsRunning(true);
    try {
      for (let attempt = 0; attempt < 30; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 10000));
        const res = await fetch(`${API}/projects/${projectId}/runs/latest`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 404) continue;
        if (!res.ok) throw new Error(await res.text());
        const data = (await res.json()) as ModelResults;
        if (data.status === "completed") {
          setModelResults(data);
          setActiveTab("Impacts: Operational Analysis");
          setIsRunning(false);
          setRunPhase('idle');
          return;
        }
        if (data.status === "failed") throw new Error("Model run failed");
      }
      throw new Error("Timeout waiting for model run");
    } catch (err: any) {
      console.error(err);
      const friendlyMessage = formatErrorMessage(err.message ?? err);
      setErrorMessage(friendlyMessage);
      setShowErrorModal(true);
      setIsRunning(false);
    }
  };

  const handleRunModel = async () => {
    if (!token || !customerId || !isComplete) return;
    setModelResults(null);
    setRunPhase("submitting");
    setIsRunning(true);

    const isExisting = projectStatus === "existing";

    // Base payload from inputs + your forced overrides
    const payload = buildProjectPayload(
      { ...inputs, customer_id: customerId, existing_project_flag: isExisting, sam_profile_flag: !isExisting },
      {
        // your page's forced values
        ppa_flag: true,
        ppa_percentage: 1.0,
        battery_flag: false,
        battery_capacity_mwh: 0.0,
        battery_charge_rate_mw: 0.0,
        battery_discharge_rate_mw: 0.0,
        battery_round_trip_eff: 1.0,
        battery_soc_min: 0.0,
        project_additional_load: 0.0,
      }
    );

    try {
      const { project_id } = await createProject(payload, token);
      await queueRun(project_id, token);
      setRunPhase("running");

      // polling logic can keep using your function; or use getLatestRun
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 10000));
        const data = await getLatestRun(project_id, token);
        if (!data) continue;
        if (data.status === "completed") {
          setModelResults(data);
          setActiveTab("Impacts: Operational Analysis");
          setIsRunning(false);
          setRunPhase("idle");
          return;
        }
        if (data.status === "failed") throw new Error("Model run failed");
      }
      throw new Error("Timeout waiting for model run");
    } catch (err: any) {
      const friendlyMessage = formatErrorMessage(err.message ?? err);
      setErrorMessage(friendlyMessage);
      setShowErrorModal(true);
      setRunPhase("idle");
      setIsRunning(false);
    }
  };

  const renderInputForKey = (key: keyof ModelInputs) => {
    if (key === "project_name") return null;
    if (boolKeys.includes(key as any)) {
      return (
        <div key={key} className="flex flex-col">
          <label className="mb-1 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              name={key}
              checked={!!inputs[key]}
              onChange={handleChange}
              className="mr-2 accent-green-600"
            />
            {key
              .split("_")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ")}
          </label>
        </div>
      );
    }
    const isDate = dateKeys.includes(key as any);
    return (
      <div key={key} className="flex flex-col">
        <label htmlFor={key} className="mb-1 text-sm font-medium text-gray-700">
          {key
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ")}
        </label>
        <input
          id={key}
          name={key}
          type={isDate ? "date" : "text"}
          value={inputs[key] as string}
          onChange={handleChange}
          className="w-full rounded border p-2"
        />
      </div>
    );
  };

  /* ——— Results Rendering ——— */
  const handleExportCsv = () => {
    if (!modelResults) return;
    const rows: Record<string, any>[] = [];
    const { summary_json } = modelResults;
    if (summary_json.Impacts?.Operational_Analysis) {
      Object.entries(summary_json.Impacts.Operational_Analysis).forEach(([cat, data]) => {
        rows.push(...flattenForCsv("Impacts: Operational Analysis", cat, data));
      });
    }
    if (summary_json.Emissions?.Operational_Analysis) {
      Object.entries(summary_json.Emissions.Operational_Analysis).forEach(([cat, data]) => {
        rows.push(...flattenForCsv("Emissions: Operational Analysis", cat, data));
      });
    }
    if (summary_json.Impacts?.Cradle_to_Grave_Analysis) {
      Object.entries(summary_json.Impacts.Cradle_to_Grave_Analysis).forEach(([cat, data]) => {
        rows.push(...flattenForCsv("Impacts: Cradle-to-Grave", cat, data));
      });
    }
    if (summary_json.Generation) {
      Object.entries(summary_json.Generation).forEach(([fuel, data]) => {
        rows.push({ section: "Generation", category: fuel, ...data });
      });
    }
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${inputs.project_name || "results"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const TabButton = ({ label }: { label: string }) => (
    <button
      className={`px-4 py-2 rounded-t-md border-b-2 transition text-sm font-medium ${activeTab === label
        ? "border-green-600 text-green-700"
        : "border-transparent text-gray-500 hover:text-green-600 hover:border-green-300"
        }`}
      onClick={() => setActiveTab(label)}
    >
      {label}
    </button>
  );

  const renderTable = (data: JsonMap | JsonMap[]) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Object.keys(Array.isArray(data) ? data[0] : data).map((key) => (
              <th
                key={key}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {key.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.isArray(data) ? (
            data.map((row, idx) => (
              <tr key={idx}>
                {Object.values(row).map((val, i) => (
                  <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {typeof val === "number" ? val.toFixed(2) : String(val)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              {Object.values(data).map((val, i) => (
                <td key={i} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {typeof val === "number" ? val.toFixed(2) : String(val)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderActiveSection = () => {
    if (!modelResults) return null;
    const { summary_json } = modelResults;
    switch (activeTab) {
      case "Impacts: Operational Analysis":
        return summary_json.Impacts?.Operational_Analysis ? (
          <Fragment>
            {Object.entries(summary_json.Impacts.Operational_Analysis).map(([cat, data]) => (
              <div key={cat} className="mb-6">
                <h5 className="text-md font-medium text-gray-700 mb-2">
                  {cat.replace(/_/g, " ")}
                </h5>
                {renderTable(data)}
              </div>
            ))}
          </Fragment>
        ) : null;
      case "Emissions: Operational Analysis":
        return summary_json.Emissions?.Operational_Analysis ? (
          <Fragment>
            {Object.entries(summary_json.Emissions.Operational_Analysis).map(([cat, data]) => (
              <div key={cat} className="mb-6">
                <h5 className="text-md font-medium text-gray-700 mb-2">
                  {cat.replace(/_/g, " ")}
                </h5>
                {renderTable(data)}
              </div>
            ))}
          </Fragment>
        ) : null;
      case "Impacts: Cradle-to-Grave":
        return summary_json.Impacts?.Cradle_to_Grave_Analysis ? (
          <Fragment>
            {Object.entries(summary_json.Impacts.Cradle_to_Grave_Analysis).map(([cat, data]) => (
              <div key={cat} className="mb-6">
                <h5 className="text-md font-medium text-gray-700 mb-2">
                  {cat.replace(/_/g, " ")}
                </h5>
                {renderTable(data)}
              </div>
            ))}
          </Fragment>
        ) : null;
      case "Generation":
        return summary_json.Generation ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Generation Type
                  </th>
                  {Object.keys(Object.values(summary_json.Generation)[0] || {}).map((header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {header.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(summary_json.Generation).map(([fuelType, values]) => (
                  <tr key={fuelType}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fuelType.replace(/_/g, " ")}
                    </td>
                    {Object.values(values as JsonMap).map((val, idx) => (
                      <td key={idx} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {typeof val === "number" ? val.toFixed(1) : String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!modelResults) return null;
    const availableTabs: string[] = [];
    if (modelResults.summary_json.Impacts?.Operational_Analysis) availableTabs.push("Impacts: Operational Analysis");
    if (modelResults.summary_json.Emissions?.Operational_Analysis) availableTabs.push("Emissions: Operational Analysis");
    if (modelResults.summary_json.Impacts?.Cradle_to_Grave_Analysis)
      availableTabs.push("Impacts: Cradle-to-Grave");
    if (modelResults.summary_json.Generation) availableTabs.push("Generation");

    // Ensure activeTab is valid
    if (!availableTabs.includes(activeTab)) setActiveTab(availableTabs[0]);

    return (
      <section className="mt-8 bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-green-700">Model Results</h3>
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 px-4 py-2 border rounded text-sm font-medium text-green-700 border-green-600 hover:bg-green-50"
          >
            Export CSV
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b mb-4 flex space-x-2">
          {availableTabs.map((tab) => (
            <TabButton key={tab} label={tab} />
          ))}
        </div>

        {/* Active content */}
        {renderActiveSection()}
      </section>
    );
  };

  // Error Modal Component
  const ErrorModal = () => {
    if (!showErrorModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
          <h3 className="mb-4 text-lg font-medium text-red-600">Error</h3>
          <p className="mb-6 text-gray-700">{errorMessage}</p>
          <div className="flex justify-end">
            <button
              onClick={() => setShowErrorModal(false)}
              className="rounded bg-gray-200 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add this helper function near the top of your Model component
  const formatErrorMessage = (error: string): string => {
    try {
      // Check if error is a JSON string
      if (error.startsWith('{') && error.endsWith('}')) {
        const errorObj = JSON.parse(error);

        // Handle specific error cases with user-friendly messages
        if (errorObj.detail === "Project name already exists for this customer") {
          return "A project with this name already exists. Please use a different project name.";
        }

        // For other errors, just return the detail without JSON formatting
        if (errorObj.detail) {
          return errorObj.detail;
        }
      }

      // Remove any JSON syntax if present
      return error.replace(/[{}"]/g, '').replace(/detail:/i, '').trim();
    } catch {
      // If JSON parsing fails, just clean up the string
      return error.replace(/[{}"]/g, '').replace(/detail:/i, '').trim();
    }
  };

  /* ——— Render ——— */
  return (
    <div className="pt-0 px-6 pb-8 w-full max-w-none">
      <h2 className="text-2xl font-semibold mb-6 text-green-700">Run Model</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleRunModel();
        }}
        className="space-y-8"
      >
        {/* General */}
        <section className="relative">
          <h3 className="mb-3 text-lg font-medium text-green-700">General Information</h3>
          <div className={`${GRID} items-start`}>
            <div className="flex flex-col">
              <label htmlFor="project_name" className="mb-1 text-sm font-medium text-gray-700">
                Project Name
              </label>
              <input
                id="project_name"
                name="project_name"
                value={inputs.project_name}
                onChange={handleChange}
                className="w-full rounded border p-2"
              />
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700">Project Status</label>
              <div className="flex flex-row gap-6">
                {PROJECT_STATUS_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-sm font-medium">
                    <input
                      type="radio"
                      name="project_status"
                      value={opt.value}
                      checked={projectStatus === opt.value}
                      onChange={handleProjectStatusChange}
                      className="accent-green-600"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-700">Hourly Generation Profile</label>
              {projectStatus === "existing" ? (
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="gen_profile_upload"
                    className="inline-flex items-center gap-2 px-4 py-2 border rounded cursor-pointer text-sm font-medium text-green-700 border-green-600 hover:bg-green-50"
                  >
                    <FiUpload className="w-5 h-5" /> Upload CSV
                  </label>
                  <input
                    id="gen_profile_upload"
                    type="file"
                    accept=".csv"
                    onChange={handleGenProfileUpload}
                    className="hidden"
                  />
                  {inputs.gen_hour_profile_mw.length > 0 && (
                    <span className="text-green-700 text-sm">
                      {inputs.gen_hour_profile_mw.length} rows loaded
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 text-sm">
                  Automatically generated for Prospective projects
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Dynamic Sections */}
        {SECTIONS.slice(1).map((section) => (
          <section key={section.title} className="relative">
            <hr className="mb-8 border-t border-gray-200" />
            <h3 className="mb-3 text-lg font-medium text-green-700">{section.title}</h3>
            <div className={GRID}>{section.keys.map(renderInputForKey)}</div>
          </section>
        ))}

        {/* Submit */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={!isComplete || isRunning}
            className={`inline-flex items-center gap-2 rounded px-6 py-2 text-sm font-semibold text-white transition ${isComplete && !isRunning
              ? "bg-green-600 hover:bg-green-700"
              : "cursor-not-allowed bg-gray-300"
              }`}
          >
            {isRunning ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <FiPlay /> Run Model
              </>
            )}
          </button>
        </div>
      </form>

      {/* Results & Loading */}
      {runPhase !== 'idle' && (
        <div
          className="mt-8 p-4 border border-blue-200 bg-blue-50 rounded text-center"
        >
          <div className="animate-pulse flex flex-col items-center justify-center">
            <svg
              className="animate-spin h-8 w-8 text-blue-600 mb-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>

            {runPhase === 'submitting' && (
              <p className="text-blue-700 font-medium">Submitting project…</p>
            )}
            {runPhase === 'running' && (
              <p className="text-blue-700 font-medium">Model running – this may take several minutes…</p>
            )}
          </div>
        </div>
      )}

      {renderResults()}

      {/* Error Modal */}
      <ErrorModal />
    </div>
  );
}